import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { APP } from '@/constants/testIds';
import { toast } from 'sonner';
import { requestNotificationPermission, fireLocalNotification } from '@/lib/pwa';
import {
  UserPlus, Sprout, Cloud, Droplets, LineChart, MapPin, Ruler,
  CalendarClock, ArrowRight, Plus, ChevronLeft, Activity, Bell, BellRing,
} from 'lucide-react';

const ADV_LABEL = {
  suitability: 'Suitability',
  crop_selection: 'Crop selection',
  crop_selection_hires: 'HiRes crop selection',
  sowing_calendar: 'Sowing calendar',
  sowing_isat: 'Weather sowing',
  weather: 'Weather',
  irrigation: 'Irrigation',
  nutrient: 'Nutrient',
  growth: 'Growth (NDVI)',
  ndvi: 'NDVI',
  harvest: 'Harvest',
  market_msp: 'MSP',
  market_mandi: 'Mandi',
  market_mandi_compare: 'Mandi compare',
  land_cover: 'Land cover',
  dndc: 'DNDC',
};

const timeAgo = (iso) => {
  if (!iso) return 'No advisories yet';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'No advisories yet';
  const diff = (Date.now() - then) / 1000;
  if (diff < 60)        return 'just now';
  if (diff < 3600)      return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} days ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Landing = ({ farmers, onSelectFarmer, onSelectFarm, onFarmerCreated }) => {
  const [showRegister, setShowRegister] = useState(farmers.length === 0);
  const [form, setForm] = useState({ name: '', phone: '', village: '', mandal: '', district: '' });
  const [saving, setSaving] = useState(false);

  // Mini dashboard preview state (when farmer picked from dropdown)
  const [previewFarmerId, setPreviewFarmerId] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [loadingDash, setLoadingDash] = useState(false);
  const [notifState, setNotifState] = useState(
    typeof window !== 'undefined' && 'Notification' in window ? Notification.permission : 'unsupported'
  );

  const enableAlerts = async () => {
    const p = await requestNotificationPermission();
    setNotifState(p);
    if (p === 'granted') {
      await fireLocalNotification({
        title: '🌱 AgriAgent alerts enabled',
        body: "You'll get sowing, weather and mandi alerts for your farms.",
        tag: 'agriagent-onboarding',
      });
      toast.success('Farm alerts enabled — you can install AgriAgent from the banner too');
    } else if (p === 'denied') {
      toast.error('Notifications blocked — enable them in browser Settings → Site permissions');
    }
  };

  useEffect(() => {
    if (!previewFarmerId) { setDashboard(null); return; }
    setLoadingDash(true);
    api.farmerDashboard(previewFarmerId)
      .then(setDashboard)
      .catch(() => toast.error('Could not load farmer dashboard'))
      .finally(() => setLoadingDash(false));
  }, [previewFarmerId]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Farmer name is required'); return; }
    setSaving(true);
    try {
      const f = await api.createFarmer(form);
      await onFarmerCreated();
      onSelectFarmer(f.id);
      toast.success(`Registered ${f.name}`);
    } catch (e) {
      toast.error('Registration failed');
    } finally { setSaving(false); }
  };

  // ─── Mini Dashboard view ──────────────────────────────────────────────
  if (previewFarmerId) {
    return (
      <div className="animate-fade-up" data-testid={APP.farmerDashboard}>
        <div className="flex items-center justify-between mb-6">
          <Button
            variant="ghost"
            onClick={() => setPreviewFarmerId(null)}
            data-testid={APP.switchFarmerBtn}
            className="text-icrisat-green hover:bg-icrisat-green/5 -ml-3"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Switch farmer
          </Button>
        </div>

        <section className="bg-field-pattern rounded-3xl px-6 sm:px-12 py-10 mb-6 relative overflow-hidden">
          <p className="text-icrisat-green font-medium tracking-widest uppercase text-xs mb-2">Welcome back</p>
          <h1 className="font-display text-3xl sm:text-4xl text-icrisat-green">
            {loadingDash ? 'Loading your farms…' : `Namaste, ${dashboard?.farmer?.name || ''} 👋`}
          </h1>
          {dashboard?.farmer && (
            <p className="text-sm sm:text-base mt-3 text-icrisat-earth">
              {[dashboard.farmer.village, dashboard.farmer.mandal, dashboard.farmer.district].filter(Boolean).join(' · ') || 'Telangana'}
              {' · '}
              <span className="text-icrisat-green font-medium">
                {dashboard.total_advisories} advisories run across {dashboard.farms.length} farm{dashboard.farms.length === 1 ? '' : 's'}
              </span>
            </p>
          )}

          {notifState !== 'unsupported' && (
            <div className="mt-4">
              {notifState === 'granted' ? (
                <span
                  data-testid="alerts-enabled-chip"
                  className="inline-flex items-center gap-2 rounded-full bg-icrisat-green/10 text-icrisat-green border border-icrisat-green/30 px-3 py-1.5 text-xs font-medium"
                >
                  <BellRing className="h-3.5 w-3.5" /> Farm alerts enabled
                </span>
              ) : (
                <button
                  onClick={enableAlerts}
                  data-testid="enable-alerts-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-white/70 text-icrisat-green border border-icrisat-green/30 px-3 py-1.5 text-xs font-medium hover:bg-icrisat-green hover:text-white transition-colors"
                >
                  <Bell className="h-3.5 w-3.5" /> Enable weather & sowing alerts
                </button>
              )}
            </div>
          )}
        </section>

        {dashboard && dashboard.farms.length === 0 && (
          <Card className="border-dashed border-icrisat-green/40">
            <CardContent className="py-10 text-center space-y-4">
              <div className="mx-auto h-12 w-12 rounded-full bg-icrisat-green/10 flex items-center justify-center">
                <MapPin className="h-6 w-6 text-icrisat-green" />
              </div>
              <h3 className="font-display text-xl text-icrisat-green">No farms mapped yet</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Add your first farm on the map to start receiving sowing, weather, irrigation and mandi advisories.
              </p>
              <Button
                onClick={() => onSelectFarmer(previewFarmerId)}
                data-testid={APP.addFarmFromDashboardBtn}
                className="bg-icrisat-green hover:bg-[hsl(var(--icrisat-green-hover))] text-white"
              >
                <Plus className="h-4 w-4 mr-2" /> Add your first farm
              </Button>
            </CardContent>
          </Card>
        )}

        {dashboard && dashboard.farms.length > 0 && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {dashboard.farms.map(farm => (
                <Card
                  key={farm.id}
                  data-testid={APP.farmMiniCard}
                  className="border-icrisat-green/25 hover:border-icrisat-green transition-all hover:shadow-md group"
                >
                  <CardContent className="pt-5 pb-4 space-y-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-display text-lg text-icrisat-green truncate">{farm.name || 'Untitled farm'}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 truncate">
                          <MapPin className="h-3.5 w-3.5 shrink-0" />
                          {farm.lat?.toFixed?.(3)}, {farm.lon?.toFixed?.(3)}
                        </p>
                      </div>
                      <span className="shrink-0 text-[11px] font-medium px-2 py-1 rounded-full bg-icrisat-green/10 text-icrisat-green">
                        {farm.total_advisories} runs
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-1.5 text-icrisat-earth">
                        <Ruler className="h-3.5 w-3.5 text-icrisat-green/70" />
                        {farm.area_ha ? `${farm.area_ha} ha` : '—'}
                      </div>
                      <div className="flex items-center gap-1.5 text-icrisat-earth truncate">
                        <Droplets className="h-3.5 w-3.5 text-icrisat-green/70" />
                        {farm.irrigation_availability || '—'}
                      </div>
                      <div className="col-span-2 flex items-center gap-1.5 text-icrisat-earth truncate">
                        <Sprout className="h-3.5 w-3.5 text-icrisat-green/70" />
                        {farm.soil_type || 'Soil not set'}
                      </div>
                    </div>

                    <div className="border-t border-border/60 pt-3 space-y-1">
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <CalendarClock className="h-3.5 w-3.5" /> Last advisory
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-icrisat-green font-medium truncate">
                          {farm.last_advisory_type ? (
                            <>
                              <Activity className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
                              {ADV_LABEL[farm.last_advisory_type] || farm.last_advisory_type}
                              {farm.last_advisory_crop ? ` · ${farm.last_advisory_crop}` : ''}
                            </>
                          ) : 'No advisories yet'}
                        </p>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {timeAgo(farm.last_advisory_at)}
                        </span>
                      </div>
                    </div>

                    <Button
                      data-testid={APP.openFarmBtn}
                      onClick={() => onSelectFarm(previewFarmerId, farm.id)}
                      className="w-full bg-icrisat-green hover:bg-[hsl(var(--icrisat-green-hover))] text-white"
                    >
                      Open advisories <ArrowRight className="h-4 w-4 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-6 flex justify-center">
              <Button
                variant="outline"
                onClick={() => onSelectFarmer(previewFarmerId)}
                data-testid={APP.addFarmFromDashboardBtn}
                className="border-icrisat-green text-icrisat-green hover:bg-icrisat-green hover:text-white"
              >
                <Plus className="h-4 w-4 mr-2" /> Add another farm
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── Default Landing view ─────────────────────────────────────────────
  return (
    <div className="animate-fade-up">
      <section className="bg-field-pattern rounded-3xl px-6 sm:px-14 py-14 mb-8 relative overflow-hidden">
        <div className="max-w-3xl">
          <p className="text-icrisat-green font-medium tracking-widest uppercase text-xs mb-3">ICRISAT · Farm 360 AI</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-icrisat-green">
            One agent. Every crop-decision, from sowing to selling.
          </h1>
          <p className="text-base sm:text-lg mt-6 text-icrisat-earth max-w-2xl">
            Suitability scoring, weather-based sowing go/no-go, weather &amp; irrigation, nutrient &amp;
            harvest windows, and today's mandi price — for eight staple crops of Telangana. Register once,
            then run every advisory on your farm's coordinates.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {[
              { icon: Sprout, label: 'Sowing Advisory' },
              { icon: Cloud, label: 'Weather alerts' },
              { icon: Droplets, label: 'Irrigation' },
              { icon: LineChart, label: 'Mandi advisory' },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 bg-white/70 border border-icrisat-green/20 rounded-full px-4 py-1.5 text-sm text-icrisat-green">
                <Icon className="h-4 w-4" /> {label}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Card className="border-icrisat-green/30 shadow-sm">
        <CardContent className="pt-6 space-y-5">
          <div className="flex items-center gap-3">
            <UserPlus className="h-6 w-6 text-icrisat-green" />
            <div>
              <h2 className="font-display text-2xl text-icrisat-green">Farmer registration</h2>
              <p className="text-sm text-muted-foreground">Register or select a farmer to see their farm dashboard.</p>
            </div>
          </div>

          {farmers.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-[1fr_auto] items-end">
              <div>
                <Label className="text-sm text-icrisat-earth">Select existing farmer</Label>
                <Select onValueChange={(id) => setPreviewFarmerId(id)}>
                  <SelectTrigger data-testid={APP.farmerSelect} className="mt-1">
                    <SelectValue placeholder="Choose a farmer…" />
                  </SelectTrigger>
                  <SelectContent>
                    {farmers.map(f => (
                      <SelectItem key={f.id} value={f.id}>{f.name}{f.village ? ` · ${f.village}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                onClick={() => setShowRegister(v => !v)}
                data-testid={APP.registerFarmerBtn}
                className="border-icrisat-green text-icrisat-green hover:bg-icrisat-green hover:text-white"
              >
                {showRegister ? 'Cancel new registration' : '+ Register new farmer'}
              </Button>
            </div>
          )}

          {(showRegister || farmers.length === 0) && (
            <div className="grid gap-4 sm:grid-cols-2 pt-2 border-t border-border/60">
              <div>
                <Label>Name *</Label>
                <Input data-testid={APP.farmerForm.name} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input data-testid={APP.farmerForm.phone} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <Label>Village</Label>
                <Input data-testid={APP.farmerForm.village} value={form.village} onChange={e => setForm({ ...form, village: e.target.value })} />
              </div>
              <div>
                <Label>Mandal</Label>
                <Input data-testid={APP.farmerForm.mandal} value={form.mandal} onChange={e => setForm({ ...form, mandal: e.target.value })} />
              </div>
              <div className="sm:col-span-2">
                <Label>District</Label>
                <Input data-testid={APP.farmerForm.district} value={form.district} onChange={e => setForm({ ...form, district: e.target.value })} />
              </div>
              <div className="sm:col-span-2 flex gap-3 justify-end pt-2">
                {farmers.length > 0 && (
                  <Button variant="ghost" onClick={() => setShowRegister(false)} data-testid={APP.farmerForm.cancel}>Cancel</Button>
                )}
                <Button
                  data-testid={APP.farmerForm.submit}
                  onClick={submit}
                  disabled={saving}
                  className="bg-icrisat-green hover:bg-[hsl(var(--icrisat-green-hover))] text-white"
                >
                  {saving ? 'Registering…' : 'Register farmer'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Landing;
